import { useCallback, useContext, useEffect, useState } from 'react'

import { AccountContext } from 'core/context'

import { avatarByIndex, defaultAvatarIndex, randomAvatarIndex } from './avatar.utils'

/**
 * Хук аватарки текущего аккаунта.
 *
 * Индекс выбранной аватарки хранится в localStorage по ключу
 * `rc-avatar-<accountId>`.  При смене (randomize) шлём кастомный
 * `rc-avatar-change` event — чтобы и Header, и страница Account
 * перерисовались синхронно (storage-event не стреляет в той же вкладке).
 */
const STORAGE_PREFIX = 'rc-avatar-'
const CHANGE_EVENT = 'rc-avatar-change'

const keyFor = (id?: number | string) => `${STORAGE_PREFIX}${id ?? 'guest'}`

const readIndex = (id?: number | string): number => {
    const raw = window.localStorage.getItem(keyFor(id))
    if (raw === null) return defaultAvatarIndex(id)
    const n = parseInt(raw, 10)
    return Number.isNaN(n) ? defaultAvatarIndex(id) : n
}

export const useAvatar = () => {
    const { accountData } = useContext(AccountContext)
    const id = accountData?.id

    const [index, setIndex] = useState<number>(() => readIndex(id))

    // При смене аккаунта подхватываем его выбор.
    useEffect(() => {
        setIndex(readIndex(id))
    }, [id])

    // Синхронизация между компонентами (Header ↔ Account) в одной вкладке.
    useEffect(() => {
        const onChange = () => setIndex(readIndex(id))
        window.addEventListener(CHANGE_EVENT, onChange)
        return () => window.removeEventListener(CHANGE_EVENT, onChange)
    }, [id])

    const randomize = useCallback(() => {
        const next = randomAvatarIndex(readIndex(id))
        window.localStorage.setItem(keyFor(id), String(next))
        window.dispatchEvent(new Event(CHANGE_EVENT))
    }, [id])

    return {
        avatarUrl: avatarByIndex(index),
        randomize,
    }
}
